import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { PairSummaryDto } from '@rhcst/shared';
import { api, ApiRequestError } from '../lib/api.js';
import { PairSymbols } from '../components/PairRow.js';
import { RiskPanel } from '../components/RiskPanel.js';
import type { RiskSignalView } from '../components/RiskPanel.js';
import { EnrichmentPanel } from '../components/EnrichmentPanel.js';
import { OriginPanel } from '../components/OriginPanel.js';
import { SafetyChips } from '../components/SafetyChip.js';
import { formatEth, formatFeeTier, relativeAge, absoluteTime, tokenLabel } from '../lib/format.js';

/**
 * Pair detail.
 *
 * Shows every fact the system actually holds about a pair and nothing it does
 * not. Where a value is unknown it says so — the risk panel reads "not scored
 * yet" rather than showing a zero, because a zero would be indistinguishable
 * from a real score of zero, and in this system a score is a decision.
 */

/** Suspected/confirmed rug evidence (Phase 11, Slice 11C/11F). */
interface RugEventView {
  id: string;
  event_type: string;
  severity: string;
  current_liquidity_quote: string | null;
  peak_liquidity_quote: string | null;
  drop_from_peak_bps: number | null;
  actor_address: string | null;
  evidence_tx_hash: string | null;
  detected_at: string;
}

interface PairDetailResponse {
  pair: PairSummaryDto;
  watchlist: { id: string; notes: string | null; created_at: string } | null;
  open_positions: number;
  explorer_url: string | null;
  risk_signals: RiskSignalView[];
  rug_events: RugEventView[];
}

export function PairDetail() {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<PairDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState('');
  const [proposal, setProposal] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const result = await api.get<PairDetailResponse>(`/pairs/${id}`);
      setData(result);
      setNotes(result.watchlist?.notes ?? '');
      setError(null);
    } catch (err) {
      if (err instanceof ApiRequestError && err.status === 404) {
        setError('That pair does not exist.');
      } else if (!(err instanceof ApiRequestError && err.isUnauthorized)) {
        setError('Could not load the pair.');
      }
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  async function toggleWatch() {
    if (!data) return;
    setBusy(true);
    try {
      if (data.watchlist) {
        await api.delete(`/watchlist/${data.watchlist.id}`);
      } else {
        await api.post('/watchlist', { pair_id: data.pair.id, notes: notes || undefined });
      }
      await load();
    } catch {
      setError('Could not update the watchlist.');
    } finally {
      setBusy(false);
    }
  }

  async function saveNotes() {
    if (!data?.watchlist) return;
    setBusy(true);
    try {
      await api.patch(`/watchlist/${data.watchlist.id}`, { notes: notes || null });
      await load();
    } catch {
      setError('Could not save the note.');
    } finally {
      setBusy(false);
    }
  }

  /**
   * Queue a rescore and poll until the worker fulfils it.
   *
   * Changed in Phase 11 (ADR-056): `POST /rescore` used to return a score
   * synchronously, computed from whatever liquidity was already stored. It
   * now queues a request the worker fulfils with a real chain read, so this
   * polls `GET /rescore-status/:requestId` rather than reloading immediately.
   */
  async function rescore() {
    if (!data) return;
    setBusy(true);
    setError(null);
    try {
      const queued = await api.post<{ queued: boolean; request_id: string; status: string }>(
        `/pairs/${data.pair.id}/rescore`,
      );

      const deadline = Date.now() + 30_000;
      while (Date.now() < deadline) {
        await new Promise((resolve) => setTimeout(resolve, 1_500));

        const poll = await api.get<{
          status: string;
          error_message: string | null;
        }>(`/pairs/${data.pair.id}/rescore-status/${queued.request_id}`);

        if (poll.status === 'completed') {
          await load();
          return;
        }
        if (poll.status === 'failed') {
          setError(poll.error_message ?? 'The rescore failed.');
          return;
        }
        // Still pending/processing -- keep polling until the deadline.
      }

      setError('The rescore is taking longer than expected. Check back shortly.');
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'Could not queue a rescore.');
    } finally {
      setBusy(false);
    }
  }

  async function proposeTrade() {
    if (!data) return;
    const amount = window.prompt(
      'Spend how much ETH? An intent only proposes a trade — it must still be approved in Telegram, and every limit is re-checked before anything is signed.',
      '0.01',
    );
    if (amount === null) return;

    setBusy(true);
    setProposal(null);
    try {
      const result = await api.post<{
        created: boolean;
        intent_id: string | null;
        reason?: string;
        failed_checks?: string[];
        telegram_sent?: boolean;
      }>('/trade-intents', {
        pair_id: data.pair.id,
        spend_amount_eth: amount.trim(),
        max_slippage_bps: 500,
      });

      setProposal(
        result.created
          ? `Intent created. ${result.telegram_sent === true ? 'Approval card sent to Telegram.' : 'Telegram card was NOT sent — check the bot configuration.'}`
          : `Refused by policy: ${result.reason ?? 'no reason given'}`,
      );
    } catch (err) {
      setProposal(
        err instanceof ApiRequestError ? err.message : 'Could not create a trade intent.',
      );
    } finally {
      setBusy(false);
    }
  }

  async function setStatus(status: string) {
    if (!data) return;
    setBusy(true);
    try {
      await api.patch(`/pairs/${data.pair.id}/status`, { status });
      await load();
    } catch (err) {
      setError(
        err instanceof ApiRequestError ? err.message : 'Could not change the status.',
      );
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <div className="shell-content">
        <p className="panel-note">Loading pair…</p>
      </div>
    );
  }

  if (error !== null && data === null) {
    return (
      <div className="shell-content">
        <Link className="back-link" to="/pairs">
          ← Back to pairs
        </Link>
        <p className="notice" data-tone="alarm" role="alert">
          {error}
        </p>
      </div>
    );
  }

  if (!data) return null;

  const { pair } = data;
  const feeTier = formatFeeTier(pair.fee_tier);
  const token0 = tokenLabel(pair.token0);
  const token1 = tokenLabel(pair.token1);

  const latest = data.risk_signals[0] ?? null;
  const skipped = ((): number => {
    const r = latest?.rule_results as { rules?: Array<{ outcome?: string }> } | null;
    return (r?.rules ?? []).filter((rule) => rule.outcome === 'n/a').length;
  })();

  return (
    <div className="stack">
      <Link className="back-link" to="/pairs">
        ← Back to pairs
      </Link>

      {/* The header answers "what am I looking at" before any table does. The
          ring is verdict-coloured, so it reads before the numeral inside it. */}
      <div className="screen-head">
        <div className="row" style={{ gap: 'var(--s-5)' }}>
          {latest === null ? (
            <span className="score-ring" data-verdict={undefined}>
              <span className="score-ring-of">no
                <br />score</span>
            </span>
          ) : latest.has_veto ? (
            <span className="score-ring" data-verdict="avoid">
              <span className="veto-block">Veto</span>
            </span>
          ) : (
            <span className="score-ring" data-verdict={latest.verdict}>
              <span style={{ textAlign: 'center' }}>
                <span className="score-ring-value">{latest.score}</span>
                <br />
                <span className="score-ring-of">/100</span>
              </span>
            </span>
          )}

          <span className="stack-sm" style={{ gap: 'var(--s-1)' }}>
            <h1 className="screen-title">
              <PairSymbols pair={pair} />
            </h1>
            <span className="row" style={{ flexWrap: 'wrap' }}>
              {latest !== null && !latest.has_veto && (
                <span className="chip" data-verdict={latest.verdict}>
                  {latest.verdict}
                </span>
              )}
              {latest !== null && !latest.complete && (
                <span className="partial-chip">
                  Partial{skipped > 0 ? ` — ${skipped} skipped` : ''}
                </span>
              )}
              <span className="chip" data-tone="muted">
                {pair.status}
              </span>
              {/* v2: quick-glance lifecycle/approval state, mirroring the
                  fuller detail (with reason text) in the Lifecycle &
                  liquidity panel below. */}
              <span className="chip" data-tone={pair.lifecycle_status ? 'neutral' : 'muted'}>
                {pair.lifecycle_status ?? 'not classified'}
              </span>
              <span className="chip" data-tone={pair.approval_eligible ? 'neutral' : 'muted'}>
                {pair.approval_eligible ? 'Approval eligible' : 'Not approval eligible'}
              </span>
            </span>
          </span>
        </div>

        <div className="screen-actions">
          <button
            className="btn"
            type="button"
            onClick={() => void toggleWatch()}
            disabled={busy}
            aria-pressed={data.watchlist !== null}
          >
            {data.watchlist ? 'Watching' : 'Watch'}
          </button>
          <button className="btn" type="button" onClick={() => void rescore()} disabled={busy}>
            {busy ? 'Working…' : 'Rescore'}
          </button>
        </div>
      </div>

      {error !== null && (
        <p className="notice" data-tone="alarm" role="alert">
          {error}
        </p>
      )}

      <section className="panel">
        <div className="panel-head">
          <h2 className="panel-title">Pair facts</h2>
        </div>

        <div className="panel-body">
          <dl className="facts">
            <div className="fact">
              <dt className="fact-key">Pair address</dt>
              <dd className="fact-value">{pair.address}</dd>
            </div>
            <div className="fact">
              <dt className="fact-key">Factory</dt>
              <dd className="fact-value">{pair.factory_address}</dd>
            </div>
            <div className="fact">
              <dt className="fact-key">First seen</dt>
              <dd className="fact-value" title={absoluteTime(pair.first_seen_at)}>
                {relativeAge(pair.first_seen_at)} ago
              </dd>
            </div>
            <div className="fact">
              <dt className="fact-key">Created on chain</dt>
              <dd className="fact-value">{absoluteTime(pair.created_at_chain)}</dd>
            </div>
            <div className="fact">
              <dt className="fact-key">Created block</dt>
              <dd className="fact-value">{pair.created_block ?? '—'}</dd>
            </div>
            <div className="fact">
              <dt className="fact-key">Liquidity</dt>
              <dd className="fact-value">
                {pair.liquidity_eth === null ? 'Not measured' : `${formatEth(pair.liquidity_eth)} ETH`}
              </dd>
            </div>
            {feeTier !== null && (
              <div className="fact">
                <dt className="fact-key">Fee tier</dt>
                <dd className="fact-value">{feeTier}</dd>
              </div>
            )}
            <div className="fact">
              <dt className="fact-key">Quote asset</dt>
              <dd className="fact-value">
                {pair.quote_token_address ?? 'None recognised'}
              </dd>
            </div>
            <div className="fact">
              <dt className="fact-key">Open positions</dt>
              <dd className="fact-value">{data.open_positions}</dd>
            </div>
          </dl>
        </div>
      </section>

      <div className="grid-2">
        <section className="panel">
          <div className="panel-head">
            <h2 className="panel-title">Tokens</h2>
          </div>
          <div className="panel-body stack-2">
            {[pair.token0, pair.token1].map((token, index) => {
              const label = index === 0 ? token0 : token1;
              return (
                <dl className="facts" key={token.address}>
                  <div className="fact">
                    <dt className="fact-key">
                      Token{index} {token.address === pair.quote_token_address ? '(quote)' : ''}
                    </dt>
                    <dd className="fact-value">
                      <span className={label.isSymbol ? undefined : 'token-unknown'}>
                        {token.symbol ?? 'Symbol unreadable'}
                      </span>
                      {token.name ? ` — ${token.name}` : ''}
                    </dd>
                    <dd className="fact-value">{token.address}</dd>
                    <dd className="fact-value">
                      <span className="chip" data-tone={token.metadata_status === 'ok' ? 'muted' : 'alarm'}>
                        metadata {token.metadata_status}
                      </span>
                      {token.decimals !== null ? (
                        <span className="chip" data-tone="muted">
                          {token.decimals} decimals
                        </span>
                      ) : null}
                      {token.is_blocklisted ? (
                        <span className="chip" data-tone="alarm">
                          Blocklisted
                        </span>
                      ) : null}
                    </dd>
                    <dd className="fact-value">
                      <SafetyChips token={token} variant="full" />
                    </dd>
                  </div>
                </dl>
              );
            })}
          </div>
        </section>

        <RiskPanel
          signals={data.risk_signals}
          onRescore={() => void rescore()}
          busy={busy}
        />
      </div>

      <EnrichmentPanel enrichment={pair.enrichment} />

      <OriginPanel origin={pair.origin} pair={pair} />
      {/* Phase 11, Slice 11F: lifecycle status, liquidity freshness/drop,
          rug evidence, actor reputation, and funding-path provenance --
          every fact the system holds specifically about "is this pair safe
          to trade right now", separate from the base risk score above. */}
      <section className="panel">
        <div className="panel-head">
          <h2 className="panel-title">Lifecycle & liquidity</h2>
        </div>
        <div className="panel-body stack-2">
          <div className="row" style={{ flexWrap: 'wrap', gap: 'var(--s-2)' }}>
            <span className="chip" data-tone={pair.lifecycle_status ? 'neutral' : 'muted'}>
              {pair.lifecycle_status ?? 'not yet classified'}
            </span>
            <span className="chip" data-tone={pair.approval_eligible ? 'neutral' : 'muted'}>
              {pair.approval_eligible ? 'Approval eligible' : 'Not approval eligible'}
            </span>
            {pair.liquidity_status && (
              <span className="chip" data-tone="muted">
                liquidity: {pair.liquidity_status}
              </span>
            )}
          </div>

          {/* The explicit "why" line, verbatim -- never a client-side
              interpretation of the underlying fields above it. */}
          <p className="panel-note">
            {pair.approval_block_reason ??
              (pair.lifecycle_status_reason ??
                'No block reason recorded (either eligible, or not yet evaluated).')}
          </p>

          <dl className="facts">
            <div className="fact">
              <dt className="fact-key">Peak liquidity</dt>
              <dd className="fact-value">
                {pair.peak_liquidity_eth === null
                  ? 'Not measured'
                  : `${formatEth(pair.peak_liquidity_eth)} ETH`}
              </dd>
            </div>
            <div className="fact">
              <dt className="fact-key">Previous reading</dt>
              <dd className="fact-value">
                {pair.previous_liquidity_eth === null
                  ? '—'
                  : `${formatEth(pair.previous_liquidity_eth)} ETH`}
              </dd>
            </div>
            <div className="fact">
              <dt className="fact-key">Drop from peak</dt>
              <dd className="fact-value">
                {pair.liquidity_drop_from_peak_bps === null
                  ? '—'
                  : `${(pair.liquidity_drop_from_peak_bps / 100).toFixed(1)}%`}
              </dd>
            </div>
            <div className="fact">
              <dt className="fact-key">Liquidity last checked</dt>
              <dd className="fact-value" title={absoluteTime(pair.liquidity_last_checked_at)}>
                {pair.liquidity_last_checked_at === null
                  ? 'Never'
                  : `${relativeAge(pair.liquidity_last_checked_at)} ago`}
              </dd>
            </div>
            <div className="fact">
              <dt className="fact-key">Initial LP provider</dt>
              <dd className="fact-value">{pair.initial_liquidity_provider_address ?? 'Unknown'}</dd>
            </div>
          </dl>

          {/* Actor reputation and funding-path provenance (Phase 11, Slice
              11D/11E) -- whatever this pair is connected to, not itself. */}
          <div className="row" style={{ flexWrap: 'wrap', gap: 'var(--s-2)' }}>
            <span
              className="chip"
              data-tone={
                pair.actor_reputation === null || pair.actor_reputation.risk_level === 'none'
                  ? 'muted'
                  : 'alarm'
              }
            >
              actor reputation: {pair.actor_reputation?.risk_level ?? 'no evidence'}
            </span>
            <span
              className="chip"
              data-tone={
                pair.provenance_risk_level === 'critical' || pair.provenance_risk_level === 'high'
                  ? 'alarm'
                  : 'muted'
              }
            >
              funding-path risk: {pair.provenance_risk_level}
            </span>
          </div>
          {pair.actor_reputation && pair.actor_reputation.tags.length > 0 && (
            <p className="panel-note">
              {pair.actor_reputation.address} — {pair.actor_reputation.tags.join(', ')} (
              {pair.actor_reputation.distinct_rugged_tokens_count} distinct rugged token
              {pair.actor_reputation.distinct_rugged_tokens_count === 1 ? '' : 's'})
            </p>
          )}

          {data.rug_events.length > 0 && (
            <div className="stack-2">
              <span className="fact-key">Rug / suspected-rug evidence</span>
              {data.rug_events.map((event) => (
                <p className="panel-note" key={event.id}>
                  <span className="chip" data-tone="alarm">
                    {event.event_type}
                  </span>{' '}
                  {absoluteTime(event.detected_at)} — drop{' '}
                  {event.drop_from_peak_bps === null
                    ? 'unmeasured'
                    : `${(event.drop_from_peak_bps / 100).toFixed(1)}%`}{' '}
                  from peak
                  {event.actor_address ? `, attributed to ${event.actor_address}` : ', actor not attributed'}
                </p>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="panel">
        <div className="panel-head">
          <h2 className="panel-title">Triage</h2>
        </div>
        <div className="panel-body stack-2">
          <div className="form-actions">
            <button
              className="btn btn-ghost"
              type="button"
              data-active={data.watchlist !== null}
              disabled={busy}
              onClick={() => void toggleWatch()}
            >
              {data.watchlist ? 'Remove from watchlist' : 'Add to watchlist'}
            </button>

            <button
              className="btn btn-ghost"
              type="button"
              disabled={busy || pair.status === 'tracking'}
              onClick={() => void setStatus('tracking')}
            >
              Mark tracking
            </button>

            <button
              className="btn btn-ghost"
              type="button"
              disabled={busy || pair.status === 'ignored'}
              onClick={() => void setStatus('ignored')}
            >
              Ignore
            </button>

            {pair.status !== 'new' && (
              <button
                className="btn btn-ghost"
                type="button"
                disabled={busy}
                onClick={() => void setStatus('new')}
              >
                Reset to new
              </button>
            )}

            {/* Disabled until the pair has a score: the policy gate compares
                against MIN_RISK_SCORE_TO_BUY, so an unscored pair can only ever
                be refused. */}
            <button
              className="btn btn-danger"
              type="button"
              disabled={busy || data.risk_signals.length === 0}
              onClick={() => void proposeTrade()}
              title={
                data.risk_signals.length === 0
                  ? 'Score this pair before proposing a trade'
                  : 'Creates a proposal that must be approved in Telegram'
              }
            >
              Propose trade…
            </button>
          </div>

          {proposal !== null && (
            <p className="notice" role="status">
              {proposal}
            </p>
          )}

          {data.watchlist && (
            <div className="stack-2">
              <label className="filter">
                <span className="filter-label">Watchlist note</span>
                <input
                  className="filter-input"
                  type="text"
                  value={notes}
                  maxLength={1000}
                  placeholder="Why you are watching this"
                  onChange={(event) => setNotes(event.target.value)}
                />
              </label>
              <button
                className="btn btn-ghost"
                type="button"
                disabled={busy || notes === (data.watchlist.notes ?? '')}
                onClick={() => void saveNotes()}
                style={{ justifySelf: 'start' }}
              >
                Save note
              </button>
            </div>
          )}

          {data.explorer_url !== null && (
            <p className="panel-note">
              <a className="back-link" href={data.explorer_url} target="_blank" rel="noreferrer noopener">
                Open in the block explorer ↗
              </a>
            </p>
          )}
        </div>
      </section>
    </div>
  );
}